import React, { useMemo } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Outlet,
  useLocation,
} from 'react-router-dom';

import { LayoutRegGuideV1 } from './layout-reg-guide-v1';
import { LayoutPrimary } from './layout-primary';
import { LayoutRegGuide } from './layout-reg-guide/layout-reg-guide';
import { Login } from './login';
import { ResetPassword } from './reset-password';
import { Profile } from './profile';
import { Registration } from './registration';
import { LandingPage } from './landing-page';
import { CookieBanner } from './cookie-banner';
import { MyLenses } from './my-lenses';
import { MyBonuses } from './my-bonuses';
import { PointsHowTo } from './points-how-to';
import { CookiePolicy } from './cookie-policy';
import { PageNotFound } from './page-not-found';
import { Program } from './program';
import { EcpLocator } from './ecp-locator';
import { Appointment } from './appointment/appointment.container';
import { InternalLinks } from '../../../constants';
import { VisionProfile, VisionProfilePage } from './vision-profile';
import { usePrivateRoute } from '../../../hooks/use-private-route';
import { AppLevelModals } from './app-level-modals';
import { MyOrdersPage } from './order/my-orders-page';
import { OrderCreatePage } from './order/order-create-page';
import { RegGuidePage } from './reg-guide-page';
import { RegGuidePageNew } from './reg-guide-page-new';
import { ProfileDelete } from './profile-delete';
import { EcpLocatorExternal } from './ecp-locator-external';
import { MyAcuvueIdContainer } from './my-acuvue-id/my-acuvue-id.container';
import { ArlPage } from './arl-page';
import { BrandPageVision } from './brand-pages/vision';
import { BrandPageComfort } from './brand-pages/comfort';
import { BrandPageUv } from './brand-pages/uv';
import { BrandPageMaxComfort } from './brand-pages/max-comfort';
import { BrandPageClarity } from './brand-pages/clarity';
import { BrandPageMaxUv } from './brand-pages/max-uv';
import { Brand1 } from './brand-pages/facts-about-brand/brand1';
import { BrandPopularity } from './brand-pages/facts-about-brand/brand-popularity';
import { BrandTrust } from './brand-pages/facts-about-brand/brand-trust';
import { BrandUv } from './brand-pages/facts-about-brand/brand-uv';
import { BrandComfort } from './brand-pages/facts-about-brand/brand-comfort';
import { Aom1dSimulator } from './simulators';
import { YesNo } from './brand-pages/yes-no';
import { Unsubscribe } from './unsubscribe';
import { Facts } from './facts';
import { AboutProfile } from './kids/about-profile/about-profile';
import { KidsProfile } from './kids/kids-profile';
import { NextStep } from './kids/next-step/next-step';
import { CreateKidsProfile } from './kids/create-kids-profile';
import { ViewKidsProfile } from './kids/view-kids-profile';
import { ScrollToTop } from '../../common/scroll-to-top';
import { LiveChatRoute } from './live-chat';
import { useYandexMetrika } from '../../../hooks/use-yandex-metrika';
import { PromoVideoLandingPage, PROMO_VIDEO_PAGES } from './eyes-promo-video-page';
import { ReferralModalHandler } from './referral-modal/referral-modal-handler';

// Root frame that keeps global UI outside of individual layouts
const RootFrame = (): JSX.Element => {

  useYandexMetrika(Number(process.env.REACT_APP_METRIKA_ID));

  const location = useLocation();
  const isShowCookieBanner = location.pathname !== InternalLinks.ecpLocatorExternal
    && !location.pathname.includes('live-chat');

  return (
    <>
      <ScrollToTop />
      <ReferralModalHandler />
      <Outlet />
      {isShowCookieBanner && (
        <>
          <CookieBanner />
          <AppLevelModals />
        </>
      )}
      <MyAcuvueIdContainer />
    </>
  );
};
import { AfterSurveyScreen } from './after-survey-screen';

export const App = (): JSX.Element => {
  const { getPrivateElement } = usePrivateRoute();

  const router = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <Route element={<RootFrame />}>
            <Route element={<LayoutPrimary />}>
              <Route path="/" element={<LandingPage />} />
              <Route path={InternalLinks.cookiePage} element={<CookiePolicy />} />
              <Route path={InternalLinks.programPage} element={<Program />} />
              <Route path={InternalLinks.ecpLocator} element={<EcpLocator />} />
              <Route path={InternalLinks.profilePage} element={getPrivateElement(<Profile />)} />
              <Route path={InternalLinks.myLensesPage} element={getPrivateElement(<MyLenses />)} />
              <Route path={InternalLinks.myBonusesPage} element={getPrivateElement(<MyBonuses />)} />
              <Route path={InternalLinks.pointsHowToPage} element={getPrivateElement(<PointsHowTo />)} />
              <Route path={InternalLinks.appointment} element={getPrivateElement(<Appointment />)} />
              <Route path={InternalLinks.myOrdersPage} element={getPrivateElement(<MyOrdersPage />)} />
              <Route path={InternalLinks.orderCreate} element={getPrivateElement(<OrderCreatePage />)} />
              <Route path={InternalLinks.aboutProfile} element={getPrivateElement(<AboutProfile />)} />
              <Route path={InternalLinks.personalAccount} element={getPrivateElement(<NextStep />)} />
              <Route path={InternalLinks.deleteAccount} element={<ProfileDelete />} />
              <Route path={InternalLinks.simulatorAom1d} element={<Aom1dSimulator />} />
              <Route path={InternalLinks.regGuideNew} element={<RegGuidePageNew />} />
              <Route path={InternalLinks.promoBlue} element={<PromoVideoLandingPage page={PROMO_VIDEO_PAGES.blue} />} />
              <Route path={InternalLinks.promoGreen} element={<PromoVideoLandingPage page={PROMO_VIDEO_PAGES.green} />} />
              <Route path={InternalLinks.promoGrey} element={<PromoVideoLandingPage page={PROMO_VIDEO_PAGES.grey} />} />
              <Route path={InternalLinks.promoBrown} element={<PromoVideoLandingPage page={PROMO_VIDEO_PAGES.brown} />} />
              <Route path="*" element={<PageNotFound />} />
            </Route>

            <Route element={<LayoutPrimary isAfterSurvey />}>
              <Route path={InternalLinks.usePwa} element={<AfterSurveyScreen />} />
              <Route path={InternalLinks.noAppInstall} element={<AfterSurveyScreen />} />
              <Route path={InternalLinks.uneasy} element={<AfterSurveyScreen />} />
              <Route path={InternalLinks.useSms} element={<AfterSurveyScreen />} />
              <Route path={InternalLinks.useMa} element={<AfterSurveyScreen />} />
            </Route>

            <Route element={<LayoutPrimary isBackground />}>
              <Route path={InternalLinks.login} element={<Login />} />
              <Route path={InternalLinks.registration} element={<Registration />} />
              <Route path={InternalLinks.resetPassword} element={<ResetPassword />} />
              <Route path={InternalLinks.visionProfileSurvey} element={<VisionProfile />} />
              <Route path={InternalLinks.visionProfile} element={getPrivateElement(<VisionProfilePage />)} />
              <Route path={InternalLinks.forKids} element={getPrivateElement(<KidsProfile />)} />
              <Route path={InternalLinks.viewProfile} element={getPrivateElement(<ViewKidsProfile />)} />
              <Route path={InternalLinks.createProfile} element={getPrivateElement(<CreateKidsProfile />, true)} />
            </Route>

            <Route element={<LayoutRegGuide />}>
              <Route path={InternalLinks.arl} element={<ArlPage />} />
            </Route>

            <Route element={<LayoutRegGuideV1 />}>
              <Route path={InternalLinks.regGuide} element={<RegGuidePage />} />
            </Route>
            {process.env.REACT_APP_IS_LIVE_CHAT === 'true' && <Route path={InternalLinks.liveChat} element={<LiveChatRoute />} />}
            <Route path={InternalLinks.ecpLocatorExternal} element={<EcpLocatorExternal />} />
            <Route path={InternalLinks.brandVision} element={<BrandPageVision />} />
            <Route path={InternalLinks.brandMaxComfort} element={<BrandPageMaxComfort />} />
            <Route path={InternalLinks.brandUv} element={<BrandPageUv />} />
            <Route path={InternalLinks.brandComfort} element={<BrandPageComfort />} />
            <Route path={InternalLinks.brandClarity} element={<BrandPageClarity />} />
            <Route path={InternalLinks.brandMaxUv} element={<BrandPageMaxUv />} />
            <Route path={InternalLinks.presbyopiaYes} element={(<YesNo isYes eyeKey="presbyopia" />)} />
            <Route path={InternalLinks.presbyopiaNo} element={(<YesNo eyeKey="presbyopia" />)} />
            <Route path={InternalLinks.astigmatismYes} element={(<YesNo isYes eyeKey="astigmatism" />)} />
            <Route path={InternalLinks.astigmatismNo} element={(<YesNo eyeKey="astigmatism" />)} />
            <Route path={InternalLinks.myopiaYes} element={(<YesNo isYes eyeKey="myopia" />)} />
            <Route path={InternalLinks.myopiaNo} element={(<YesNo eyeKey="myopia" />)} />
            <Route path={InternalLinks.brand1} element={<Brand1 />} />
            <Route path={InternalLinks.brandsComfort} element={<BrandComfort />} />
            <Route path={InternalLinks.brandPopularity} element={<BrandPopularity />} />
            <Route path={InternalLinks.brandTrust} element={<BrandTrust />} />
            <Route path={InternalLinks.brandsUv} element={<BrandUv />} />
            <Route path={InternalLinks.unsubscribe} element={<Unsubscribe />} />
            <Route path={InternalLinks.facts} element={<Facts />} />
          </Route>,
        ),
      ),
    [getPrivateElement],
  );

  return <RouterProvider router={router} />;
};
